import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { addDays, isSameMonth, isWithinInterval, startOfMonth } from 'date-fns'
import { useStationStore } from '@/pinia-store/useStationStore'
import { useDashboardLensStore } from '@/pinia-store/useDashboardLensStore'
import { useMatrixSummaryStore } from '@/pinia-store/useMatrixSummaryStore'
import type { LensPeriod } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import {
  anchorFromRangeStart,
  formatPeriodLabel,
  formatPeriodSubLabel,
  getPeriodRange,
  resolveNavPeriod,
  shiftAnchor
} from '../lenses/monthCalendar'
import { periodToDashboardType } from '../lenses/dashboardQuery'
import { periodUsesGrid, resolvePeriodForFlag, type MatrixCut } from '../matrix/matrixState'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useIsMobile } from '@/hooks/useIsMobile'
import { resolveMobileV3Period } from '../mobile/v3/mobileV3Period'
import { useToday } from './useToday'

/**
 * Âncora ao sair da Semana para o Mês: a semana de hoje abre o mês de hoje; qualquer
 * outra semana abre o mês que contém a maior parte dela (a quinta-feira — mesma regra
 * da numeração ISO do "Semana N" do sub-rótulo). Ex.: 28/09–04/10 → outubro.
 */
export function monthAnchorFromWeek(weekAnchor: Date, today: Date): Date {
  const { startDate, endDate } = getPeriodRange('semana', weekAnchor)
  if (isWithinInterval(today, { start: startDate, end: endDate })) return today
  return addDays(startDate, 3)
}

/** Dia em que a Timeline abre vindo de Semana/Mês (`fromNav` já resolvido pelo recorte). */
export function dayAnchorFrom(fromNav: LensPeriod, current: Date, today: Date): Date {
  if (fromNav === 'semana') {
    const { startDate, endDate } = getPeriodRange('semana', current)
    return isWithinInterval(today, { start: startDate, end: endDate }) ? today : startDate
  }
  return isSameMonth(today, current) ? today : startOfMonth(current)
}

export function useDashboardPeriod() {
  const stationStore = useStationStore()
  const lensStore = useDashboardLensStore()
  const matrixStore = useMatrixSummaryStore()
  const { listStationsQuery } = storeToRefs(stationStore)
  const { lens } = storeToRefs(lensStore)
  const { cut } = storeToRefs(matrixStore)
  const { flags } = useFeatureFlags()
  const { isMobileLayout } = useIsMobile()
  // Fallback de flag + celular v3 (SPEC-v3 Q8, R3-01): no celular v3 a Semana vira a "Escala"
  // (`mes`) ANTES de trocar a lente e buscar — uma consulta só, a S1 já monta no Mês.
  const resolvePeriod = (requested: LensPeriod): LensPeriod => {
    const period = resolvePeriodForFlag(requested, flags)
    return isMobileLayout.value && flags.enabledDashboardMobileV3
      ? resolveMobileV3Period(period)
      : period
  }

  const anchor = computed(() => {
    const start = new Date(listStationsQuery.value.startDate)
    return Number.isNaN(start.getTime())
      ? new Date()
      : anchorFromRangeStart(lens.value.period, start, cut.value)
  })
  const { today } = useToday()
  const label = computed(() =>
    formatPeriodLabel(lens.value.period, anchor.value, cut.value, today.value)
  )
  const subLabel = computed(() => formatPeriodSubLabel(lens.value.period, anchor.value, cut.value))

  async function goTo(requested: LensPeriod, date: Date): Promise<void> {
    // Flag OFF: `matriz`/`mapa` (visão salva, URL) vira Mês — sem requisição própria;
    // `timeline` vira Semana.
    const period = resolvePeriod(requested)
    if (period !== lens.value.period) lensStore.setLens({ period })
    const { startDate, endDate } = getPeriodRange(period, date, cut.value)
    // Calendário, Matriz, Mapa e Timeline têm requisição própria: a grade de `GET /stations`
    // não busca.
    stationStore.gridFetchEnabled = periodUsesGrid(period)
    await stationStore.setPeriodRange({
      type: periodToDashboardType(period, cut.value),
      startDate,
      endDate
    })
  }

  /** Âncora ao trocar entre semana e mês (Matriz: pelo recorte) e de/para a Timeline. */
  function anchorForSwitch(fromNav: LensPeriod, toNav: LensPeriod): Date {
    const today = new Date()
    // Timeline → Semana/Mês: o período que contém o dia à vista.
    if (fromNav === 'timeline') return anchor.value
    // → Timeline: hoje se estiver no período à vista, senão o 1º dia dele (da semana ou do mês).
    if (toNav === 'timeline') return dayAnchorFrom(fromNav, anchor.value, today)
    if (toNav === 'semana' && fromNav !== 'semana') {
      // Fora do mês corrente: a semana que contém o dia 4 é a primeira com a maior parte
      // no mês — assim a volta Semana → Mês (regra da maioria) cai no mesmo mês.
      return isSameMonth(today, anchor.value) ? today : addDays(startOfMonth(anchor.value), 3)
    }
    if (toNav !== 'semana' && fromNav === 'semana') return monthAnchorFromWeek(anchor.value, today)
    return anchor.value
  }

  /**
   * Âncora para sair do período atual rumo a `next` (troca de aba ou visão salva). `next` já
   * passa pelo fallback de flag, como no `goTo`. Ler ANTES de trocar `lens.period`: a âncora
   * atual depende do período à vista.
   */
  function anchorForPeriod(next: LensPeriod): Date {
    return anchorForSwitch(
      resolveNavPeriod(lens.value.period, cut.value),
      resolveNavPeriod(resolvePeriod(next), cut.value)
    )
  }

  async function setPeriod(period: LensPeriod): Promise<void> {
    await goTo(period, anchorForPeriod(period))
  }

  /** Troca o recorte da Matriz mantendo o período à vista (mesma regra de Semana ↔ Mês). */
  async function setMatrixCut(next: MatrixCut): Promise<void> {
    if (next === cut.value) return
    const inMatrix = lens.value.period === 'matriz'
    const target = anchorForSwitch(cut.value, next)
    matrixStore.setCut(next)
    if (inMatrix) await goTo('matriz', target)
  }

  const step = (direction: 1 | -1) =>
    goTo(lens.value.period, shiftAnchor(lens.value.period, anchor.value, direction, cut.value))
  const goToDate = (date: Date) => goTo(lens.value.period, date)

  return {
    anchor,
    label,
    subLabel,
    goTo,
    anchorForPeriod,
    setPeriod,
    setMatrixCut,
    step,
    goToDate
  }
}
